import { Injectable, signal, WritableSignal } from '@angular/core';

import {
  ChatMessage,
  ChatPart,
  ConnectionStatus,
  Interrupt,
  RunStatus,
  ThreadSummary,
} from '@core/tellent-assistant-types';
import { extractInterruptSentinel, sentinelToInterrupt } from '@core/tellent-assistant-util';


@Injectable({ providedIn: 'root' })
export class TellentAssistantStore {
  public readonly messages: WritableSignal<ChatMessage[]> = signal([]);
  public readonly runStatus: WritableSignal<RunStatus> = signal('idle');
  public readonly runStartedAt: WritableSignal<number | null> = signal(null);
  public readonly runDurationMs: WritableSignal<number | null> = signal(null);
  public readonly pendingInterrupt: WritableSignal<Interrupt | null> = signal(null);
  public readonly sharedState: WritableSignal<Record<string, unknown>> = signal({});
  public readonly currentThreadId: WritableSignal<string | null> = signal(null);
  public readonly threads: WritableSignal<ThreadSummary[]> = signal([]);
  public readonly lastError: WritableSignal<string | null> = signal(null);
  public readonly connectionStatus: WritableSignal<ConnectionStatus> = signal('ok');

  public markRunStart(): void {
    this.runStatus.set('running');
    this.runStartedAt.set(Date.now());
    this.lastError.set(null);
  }

  public markRunEnd(): void {
    this.runStatus.set('idle');
    const started = this.runStartedAt();
    this.runDurationMs.set(started != null ? Date.now() - started : null);
    this.runStartedAt.set(null);
  }

  public markRunError(message: string): void {
    this.runStatus.set('error');
    this.lastError.set(message);
    this.runStartedAt.set(null);
  }

  public appendUserMessage(text: string): void {
    this.messages.update(prev => [
      ...prev,
      {
        id: crypto.randomUUID(),
        role: 'user',
        parts: [{ kind: 'text', messageId: crypto.randomUUID(), content: text, done: true }],
        createdAt: new Date(),
      },
    ]);
  }

  public startTextPart(messageId: string): void {
    if (this.hasAssistantPart(p => p.kind === 'text' && p.messageId === messageId)) return;
    this.appendAssistantPart({ kind: 'text', messageId, content: '', done: false });
  }

  public appendTextDelta(messageId: string, delta: string): void {
    this.updateAssistantPart(
      p => p.kind === 'text' && p.messageId === messageId,
      p => ({
        ...(<Extract<ChatPart, { kind: 'text' }>>p),
        content: (<Extract<ChatPart, { kind: 'text' }>>p).content + delta,
      }),
    );
  }

  public finishTextPart(messageId: string): void {
    this.updateAssistantPart(
      p => p.kind === 'text' && p.messageId === messageId,
      p => ({ ...(<Extract<ChatPart, { kind: 'text' }>>p), done: true }),
    );
  }

  public startReasoningPart(messageId: string): void {
    if (this.hasAssistantPart(p => p.kind === 'reasoning' && p.messageId === messageId)) return;
    this.appendAssistantPart({ kind: 'reasoning', messageId, content: '', done: false });
  }

  public appendReasoningDelta(messageId: string, delta: string): void {
    this.updateAssistantPart(
      p => p.kind === 'reasoning' && p.messageId === messageId,
      p => ({
        ...(<Extract<ChatPart, { kind: 'reasoning' }>>p),
        content: (<Extract<ChatPart, { kind: 'reasoning' }>>p).content + delta,
      }),
    );
  }

  public finishReasoningPart(messageId: string): void {
    this.updateAssistantPart(
      p => p.kind === 'reasoning' && p.messageId === messageId,
      p => ({ ...(<Extract<ChatPart, { kind: 'reasoning' }>>p), done: true }),
    );
  }

  public startToolCall(toolCallId: string, name: string): void {
    if (this.hasAssistantPart(p => p.kind === 'tool-call' && p.toolCallId === toolCallId)) return;
    this.appendAssistantPart({
      kind: 'tool-call',
      toolCallId,
      name,
      args: null,
      status: 'running',
      startedAt: Date.now(),
    });
  }

  public finalizeOpenParts(): void {
    this.messages.update(prev =>
      prev.map(m =>
        m.role !== 'assistant'
          ? m
          : {
              ...m,
              parts: m.parts.map(p => {
                if (p.kind === 'text' && !p.done) return { ...p, done: true };
                if (p.kind === 'reasoning' && !p.done) return { ...p, done: true };
                if (p.kind === 'tool-call' && p.status === 'running') {
                  return { ...p, status: <const>'done', durationMs: Date.now() - p.startedAt };
                }
                return p;
              }),
            },
      ),
    );
  }

  public appendToolArgs(toolCallId: string, delta: string): void {
    this.updateAssistantPart(
      p => p.kind === 'tool-call' && p.toolCallId === toolCallId,
      p => {
        const prev = <Extract<ChatPart, { kind: 'tool-call' }>>p;
        const argsString = typeof prev.args === 'string' ? prev.args : '';
        return { ...prev, args: argsString + delta };
      },
    );
  }

  public finishToolCall(toolCallId: string): void {
    this.updateAssistantPart(
      p => p.kind === 'tool-call' && p.toolCallId === toolCallId,
      p => {
        const prev = <Extract<ChatPart, { kind: 'tool-call' }>>p;
        return { ...prev, status: 'done', durationMs: Date.now() - prev.startedAt };
      },
    );
  }

  public appendToolResult(toolCallId: string, content: string): void {
    let payload: unknown;
    try {
      payload = JSON.parse(content);
    } catch {
      payload = content;
    }
    const sentinel = extractInterruptSentinel(payload);
    if (sentinel) {
      this.setInterrupt(sentinelToInterrupt(sentinel));
      // Sentinel tool-results are control data, not display content.
      // The interrupt part is already appended by setInterrupt → skip tool-result.
      return;
    }
    const name = this.findToolName(toolCallId);
    this.appendAssistantPart({ kind: 'tool-result', toolCallId, name, payload });
  }

  public setInterrupt(interrupt: Interrupt): void {
    this.pendingInterrupt.set(interrupt);
    this.appendAssistantPart({
      kind: 'interrupt',
      interruptId: interrupt.id,
      reason: interrupt.reason,
      payload: interrupt.payload,
      resolved: false,
      resume: interrupt.resume,
    });
  }

  public markInterruptResolved(interruptId: string): void {
    this.pendingInterrupt.set(null);
    this.updateAssistantPart(
      p => p.kind === 'interrupt' && p.interruptId === interruptId,
      p => ({ ...(<Extract<ChatPart, { kind: 'interrupt' }>>p), resolved: true }),
    );
  }

  public replaceState(snapshot: unknown): void {
    this.sharedState.set(<Record<string, unknown>>(snapshot ?? {}));
  }

  public updateState(updater: (prev: Record<string, unknown>) => Record<string, unknown>): void {
    this.sharedState.update(updater);
  }

  public replaceMessages(messages: ChatMessage[]): void {
    this.messages.set(messages);
    this.pendingInterrupt.set(this.findLastUnresolvedInterrupt(messages));
  }

  private findLastUnresolvedInterrupt(messages: ChatMessage[]): Interrupt | null {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role !== 'assistant') continue;
      for (let j = m.parts.length - 1; j >= 0; j--) {
        const p = m.parts[j];
        if (p.kind === 'interrupt' && !p.resolved) {
          return {
            id: p.interruptId,
            reason: p.reason,
            payload: p.payload,
            resume: p.resume,
          };
        }
      }
    }
    return null;
  }

  public newThread(threadId: string): void {
    this.currentThreadId.set(threadId);
    this.messages.set([]);
    this.pendingInterrupt.set(null);
    this.sharedState.set({});
  }

  public setThreads(threads: ThreadSummary[]): void {
    this.threads.set(threads);
  }

  private appendAssistantPart(part: ChatPart): void {
    this.messages.update(prev => {
      const last = prev[prev.length - 1];
      if (last && last.role === 'assistant' && this.runStatus() === 'running') {
        return [...prev.slice(0, -1), { ...last, parts: [...last.parts, part] }];
      }
      return [
        ...prev,
        { id: crypto.randomUUID(), role: 'assistant', parts: [part], createdAt: new Date() },
      ];
    });
  }

  private hasAssistantPart(predicate: (p: ChatPart) => boolean): boolean {
    return this.messages().some(m => m.role === 'assistant' && m.parts.some(predicate));
  }

  private updateAssistantPart(
    predicate: (p: ChatPart) => boolean,
    updater: (p: ChatPart) => ChatPart,
  ): void {
    this.messages.update(prev =>
      prev.map(m =>
        m.role !== 'assistant'
          ? m
          : { ...m, parts: m.parts.map(p => (predicate(p) ? updater(p) : p)) },
      ),
    );
  }

  private findToolName(toolCallId: string): string {
    const msgs = this.messages();
    for (let i = msgs.length - 1; i >= 0; i--) {
      const match = msgs[i].parts.find(p => p.kind === 'tool-call' && p.toolCallId === toolCallId);
      if (match && match.kind === 'tool-call') return match.name;
    }
    return 'unknown';
  }
}
